import PatientsService, {
  EPatientType,
  getActualTreatmentPlanId,
  type IAvailablePlans,
  type TPatient,
  type TPatientConsentTypes,
  type TPatientSearchFilters,
  type TPatientPublicRegistrationResponse,
} from "@/api/models/patient/patient";
import { ESubjects } from "@/api/models/auth/ability";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";
import { useQueryClient, type UseMutationReturnType } from "@tanstack/vue-query";
import { computed, type MaybeRef, type MaybeRefOrGetter, type Ref, toValue, unref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { useTreatmentPlanStore, type TJobSource } from "@/stores/treatmentPlanStore";
import type { TTreatmentPlanJob } from "@/api/models/treatmentPlan/job";
import LinkService, {
  EPatientLinkedEntity,
  type ProviderListByKind,
} from "@/api/models/patient/linkEntity";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import type { TSelectFieldDynamicProps } from "@/types/selectField";

import {
  useGenericListQuery,
  useGenericPaginatedQuery,
  useGenericQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import { OrthoNeedsQueryKey } from "./useOrthoNeeds";
import { OrthoProposedPlanQueryKeys } from "./useOrthoPlan";
import { NextVisitQueryKeys } from "./useNextVisit";
// import { TreatmentPlanQueryKeys } from "../treatmentPlan/useTreatmentPlan";
import { RoomInfoQueryKeys } from "../message/useRoomInfo";
import { RoomQueryKeys } from "../message/useRoom";
import { PatientFilesQuerykey } from "./usePatientFiles";
import { TreatmentPlanJobQueryKeys } from "../treatmentPlan/useTreatmentPlanJob";
import { PatientTreatmentPlanQueryKeys } from "./usePatientTreatmentPlan";
import PatientsTreatmentService, {
  type TTreatmentPlanWithAvailableJobs,
} from "@/api/models/patient/treatmentPlan";
import { TreatmentPlanQueryKeys } from "../treatmentPlan/useTreatmentPlan";

export { defaultPageSize } from "@/composables/shared/filters/useTableFilter";

type RefetchPatientCardOptions = {
  refetchBackLoopJobs?: boolean;
};

export const PatientQueryKeys = createQueryKeys(PatientsService.queryKey, (key) => ({
  list: key<TPatientSearchFilters>("list"),
  withReferralList: key<TPatientSearchFilters>("with-referral"),
  detail: key<string>("detail"),
  availablePlans: key<IAvailablePlans>("availablePlans"),
}));

type TProviderFilter = TPatientSearchFilters & { patient_id: string } & {
  linked_entity_type: EPatientLinkedEntity;
  job_id?: string;
};

export const PatientProviderQueryKeys = createQueryKeys(LinkService.queryKey, (key) => ({
  providers: key<Partial<TProviderFilter>>("providers"),
}));

export const PatientConsentTypeQueryKeys = createQueryKeys(
  PatientsService.consentTypes.queryKey,
  (key) => ({
    // Public and authenticated reads hit different endpoints and are cached separately.
    list: key("list"),
    publicList: key("public-list"),
  })
);

export const usePatientsPaginatedFiltered = (
  params: TOwnedFilterParams<TPatientSearchFilters> = {}
) => {
  return useGenericPaginatedQuery<TPatient, Partial<TPatientSearchFilters>>({
    queryKey: PatientQueryKeys.list(),
    params,
    fetcher: (fetchParams) =>
      PatientsService.getPaginatedFiltered({}, fetchParams.filters, fetchParams.pageParam),
    permission: $permSubjects.PATIENT__PATIENT,
  });
};

export const usePatientsPaginatedWithReferral = (
  params: TOwnedFilterParams<TPatientSearchFilters> = {}
) => {
  return useGenericPaginatedQuery<TPatient, Partial<TPatientSearchFilters>>({
    queryKey: PatientQueryKeys.withReferralList(),
    params,
    fetcher: (fetchParams) =>
      PatientsService.getPaginatedWithReferral({}, fetchParams.filters, fetchParams.pageParam),
    permission: $permSubjects.PATIENT__PATIENT,
  });
};

export const usePatient = (
  params: MaybeRefOrGetter<TItemQueryComposableParams<string>>,
  options: TItemQueryBehaviorOptions<TPatient> = {}
) => {
  const route = useRoute();
  const isMyReferral = route.query.my_referrals;

  return useGenericQuery<TPatient, string>({
    queryKey: PatientQueryKeys.detail(),
    fetcher: async (f) => PatientsService.get({ id: f }, { my_referrals: isMyReferral }),
    params,
    permission: $permSubjects.PATIENT__PATIENT,
    ...options,
    options: {
      retry: false,
    },
  });
};

export const usePatientQueryItem = (
  params: TItemQueryComposableParams<string> = { enabled: true },
  options: TItemQueryBehaviorOptions<TPatient> = {}
) => {
  return useGenericQuery<TPatient, string>({
    queryKey: PatientQueryKeys.list(),
    params,
    fetcher: (id) => PatientsService.get({ id: String(id) }),
    permission: $permSubjects.PATIENT__PATIENT,
    ...options,
  });
};

export function usePatientCardRefetch() {
  const queryClient = useQueryClient();
  const availableTreatmentPlansQueryKey = PatientTreatmentPlanQueryKeys.availableTreatmentPlans();

  const hasActiveAvailableTreatmentPlansQuery = (patientId: string) =>
    queryClient
      .getQueryCache()
      .findAll({ queryKey: availableTreatmentPlansQueryKey })
      .some((query) => {
        const filters = unref(query.queryKey[availableTreatmentPlansQueryKey.length]) as
          | { patientId?: string }
          | undefined;
        return (
          filters?.patientId === patientId &&
          (query.isActive() || query.state.fetchStatus === "fetching")
        );
      });

  const refreshAvailablePlans = (patientId: string) => {
    const queryKey = PatientTreatmentPlanQueryKeys.availableTreatmentPlans({ patientId });

    // A component owns the query, but it is usually *disabled* right now: its `enabled` only
    // flips true once this refetch reveals the patient has no active plan. A disabled observer
    // never fetches on its own, so returning early here left a stale empty list in place.
    // Marking it stale instead makes it fetch the moment it re-enables. `refetchType: "none"`
    // is essential: the re-enable is itself gated behind the jobs refetch that this call is
    // awaited in front of, so awaiting a refetch here stalls the whole card refetch chain —
    // and with it the dialog close in `handleActivateTreatmentPlan`.
    if (hasActiveAvailableTreatmentPlansQuery(patientId)) {
      return queryClient.invalidateQueries({ queryKey, refetchType: "none" });
    }

    return queryClient.fetchQuery({
      queryKey,
      staleTime: 0,
      queryFn: async () =>
        (await PatientsTreatmentService.listOfAvailableTreatmentPlans({ patientId })).data,
    });
  };

  const refetchPatientQuestionnaires = async (patientId: string) => {
    await queryClient.refetchQueries({
      queryKey: PatientQueryKeys.detail(patientId),
    });

    const data = queryClient.getQueryData(PatientQueryKeys.detail(patientId)) as
      | TPatient
      | undefined;
    const treatmentPlanId = getActualTreatmentPlanId(data);

    await Promise.allSettled([
      queryClient.refetchQueries({
        queryKey: OrthoNeedsQueryKey.listOfPatientOrthoNeeds({ patient: patientId }),
      }),
      queryClient.refetchQueries({
        queryKey: PatientFilesQuerykey.listOfPatientFiles({ patient_id: patientId }),
      }),
      queryClient.refetchQueries({
        queryKey: OrthoProposedPlanQueryKeys.listOfPatientOrthoProposedPlans({
          patient: patientId,
        }),
      }),
      queryClient.refetchQueries({
        queryKey: NextVisitQueryKeys.listOfNextVisits({
          patient: patientId,
        }),
      }),
      treatmentPlanId
        ? queryClient.invalidateQueries({
            queryKey: TreatmentPlanQueryKeys.detail(treatmentPlanId),
          })
        : Promise.resolve(),
      !!patientId && refreshAvailablePlans(patientId),
    ]);
  };

  const fetchAndSetJobsStore = <T = TTreatmentPlanJob>(
    queryKey: readonly any[],
    target: TJobSource,
    transform?: (data: T[]) => TTreatmentPlanJob[]
  ) => {
    const store = useTreatmentPlanStore();
    const raw = queryClient.getQueryData<T[]>(queryKey);
    const jobsData = raw
      ? transform
        ? transform(raw)
        : (raw as unknown as TTreatmentPlanJob[])
      : undefined;
    const availableJobs = jobsData ?? store.availableJobsBySource[target] ?? [];
    store.registerJobs(target, availableJobs);
  };

  const refetchPatientTreatmentPlanJobs = async (
    patientId: string,
    options: RefetchPatientCardOptions = {}
  ) => {
    const refetchStartedAt = Date.now();
    const data = queryClient.getQueryData(PatientQueryKeys.detail(patientId)) as
      | TPatient
      | undefined;
    const treatmentPlanId = getActualTreatmentPlanId(data);
    const refetchBackLoopJobs = async () => {
      if (!treatmentPlanId) return;

      const backLoopJobsQueryKey = TreatmentPlanJobQueryKeys.availableBackLoopJobs({
        treatment_plan: treatmentPlanId,
      });
      const existingQueries = queryClient.getQueryCache().findAll({
        queryKey: backLoopJobsQueryKey,
      });
      const alreadyCurrent = existingQueries.some(
        (query) =>
          query.state.fetchStatus === "fetching" || query.state.dataUpdatedAt >= refetchStartedAt
      );

      if (alreadyCurrent) {
        await queryClient.invalidateQueries({
          queryKey: backLoopJobsQueryKey,
          refetchType: "none",
        });
        return;
      }

      await queryClient.refetchQueries({
        queryKey: backLoopJobsQueryKey,
      });
    };

    await Promise.allSettled([
      queryClient.refetchQueries({
        queryKey: PatientTreatmentPlanQueryKeys.treatmentPlansJobsOfPatient({ patientId }),
      }),
      // Chat timeline only when mounted (active observer); jobs-available refetch drives actions.
      queryClient.refetchQueries({
        queryKey: PatientTreatmentPlanQueryKeys.listOfTreatmentJobs({ patientId }),
        type: "active",
      }),
      options.refetchBackLoopJobs === false ? Promise.resolve() : refetchBackLoopJobs(),
    ]);

    fetchAndSetJobsStore<TTreatmentPlanWithAvailableJobs>(
      PatientTreatmentPlanQueryKeys.treatmentPlansJobsOfPatient({ patientId }),
      "main",
      (data) => data.flatMap((item) => item.jobs)
    );
    if (treatmentPlanId) {
      fetchAndSetJobsStore(
        TreatmentPlanJobQueryKeys.availableBackLoopJobs({ treatment_plan: treatmentPlanId }),
        "backLoop"
      );
    }
  };

  const refetchPatientCard = async (patientId: string, options: RefetchPatientCardOptions = {}) => {
    await refetchPatientQuestionnaires(patientId);
    await refetchPatientTreatmentPlanJobs(patientId, options);
  };

  const refetchPatientRooms = async (patientId: string) => {
    await Promise.allSettled([
      queryClient.refetchQueries({
        queryKey: RoomInfoQueryKeys.paginatedList({ patientId }),
      }),
      // Room list has no patient filter; only refetch mounted infinite queries.
      queryClient.refetchQueries({
        queryKey: RoomQueryKeys.paginated(),
        type: "active",
      }),
    ]);
  };

  const refetchPatientJobs = async (patientId: string) => {
    await refetchPatientTreatmentPlanJobs(patientId);
  };

  const refetchPatientJobsAndFiles = async (patientId: string) => {
    await Promise.allSettled([
      refetchPatientTreatmentPlanJobs(patientId),
      queryClient.refetchQueries({
        queryKey: PatientFilesQuerykey.listOfPatientFiles({ patient_id: patientId }),
      }),
    ]);
  };

  return {
    refetchPatientCard,
    refetchPatientQuestionnaires,
    refetchPatientTreatmentPlanJobs,
    refetchPatientRooms,
    refetchPatientJobs,
    refetchPatientJobsAndFiles,
  };
}

export function useCreatePatient(veeSetErrors?: any) {
  return useGenericMutation<TPatient, Partial<TPatient>>({
    mutationFn: (patient) => PatientsService.create({}, patient as TPatient),
    feature: "patient-create",
    invalidates: [PatientQueryKeys.list()],
    params: { veeSetErrors },
  });
}

export function useUpdatePatient(veeSetErrors?: any) {
  const route = useRoute();
  const isMyReferral = route.query.my_referrals;

  return useGenericMutation<TPatient, Partial<TPatient> & { id: string }>({
    mutationFn: (patient) =>
      PatientsService.update({ id: patient.id }, patient, { my_referrals: isMyReferral }),
    feature: "patient-update",
    // Write the PATCH body into the active detail cache so detail sidebars update
    // immediately; invalidate still refetches for consistency.
    sets: (_data, vars) => [PatientQueryKeys.detail(vars.id)],
    invalidates: (vars) => [PatientQueryKeys.list(), PatientQueryKeys.detail(vars.id)],
    params: { veeSetErrors },
  });
}

export function usePatientPublicMutation(practiceId: string, veeSetErrors?: any) {
  return useGenericMutation<TPatientPublicRegistrationResponse, Partial<TPatient>>({
    mutationFn: (patient) =>
      PatientsService.createPublicPatient({}, patient, { practice_id: practiceId }),
    feature: "patient-public-mutation",
    params: { veeSetErrors },
  });
}

export function usePatientQRPublicMutation(practiceQR: string, veeSetErrors?: any) {
  return useGenericMutation<TPatientPublicRegistrationResponse, Partial<TPatient>>({
    mutationFn: (patient) =>
      PatientsService.createPublicPatient({}, patient, { practice_qr: practiceQR }),
    feature: "patient-qr-public-mutation",
    params: { veeSetErrors },
  });
}

export function usePatientValidateMutation(veeSetErrors?: any) {
  return useGenericMutation<TPatient, Partial<TPatient>>({
    mutationFn: (patient) => PatientsService.validate({ id: patient.id as string }, patient),
    feature: "patient-validate-mutation",
    params: { veeSetErrors },
  });
}

export function useSendDmLoginLinkMutation() {
  return useGenericMutation<unknown, { patientId: string; jobId?: string }>({
    mutationFn: ({ patientId, jobId }) =>
      PatientsService.sendDmLoginLink({ id: patientId }, {}, { job_id: jobId }),
    feature: "patient-send-dm-login-link",
    params: {
      neededKey: ["patientId", "jobId"],
      permissions: {
        create: ESubjects.PATIENT__PATIENT,
      },
    },
  });
}

export function useDeletePatientMutation(): UseMutationReturnType<void, Error, string, unknown> {
  return useGenericMutation<void, string>({
    mutationFn: (id: string) => PatientsService.remove({ id }),
    feature: "delete-patient",
    invalidates: [PatientQueryKeys.list(), RoomQueryKeys.paginated()],
    removes: (id) => [PatientQueryKeys.detail(id)],
    params: {
      permissions: {
        delete: ESubjects.PATIENT__PATIENT,
      },
    },
  });
}

export const usePatientConsentTypes = (usePublic?: boolean) => {
  return useGenericListQuery<TPatientConsentTypes>({
    queryKey: usePublic
      ? PatientConsentTypeQueryKeys.publicList()
      : PatientConsentTypeQueryKeys.list(),
    fetcher: () =>
      usePublic
        ? PatientsService.consentTypes.getAllPublic({})
        : PatientsService.consentTypes.getAll(),
    // Consent types are static config. On the throttled public endpoints a re-fetch per
    // page load is what pushes anonymous forms over the rate limit.
    staleTime: Infinity,
  });
};

export const usePatientLanguages = (patient: Ref<TPatient | undefined>) => {
  const { t } = useI18n();

  const languages = computed(() => {
    let languageCodes = [patient.value?.language ?? "-", patient.value?.language_2 ?? "-"];

    return languageCodes.map((code) => {
      if (code !== "-") {
        return t(`models.patient.card.general.languagesKeys.${code}`);
      }
      return code;
    });
  });

  return {
    languages,
  };
};

export const usePatientProviderList = (
  filters?: MaybeRefOrGetter<Partial<TProviderFilter>>,
  enabled: MaybeRef<boolean> = true
) => {
  return useGenericListQuery<
    ProviderListByKind[keyof ProviderListByKind][number],
    Partial<TProviderFilter>
  >({
    queryKey: PatientProviderQueryKeys.providers(),
    params: { filters, enabled },
    fetcher: (f) =>
      LinkService.listProviders(f?.patient_id!, f?.linked_entity_type!, {
        ...f,
        patient_id: undefined,
        linked_entity_type: undefined,
      }),
    noRetryStatusCodes: [400, 404],
    neededKey: ["patient_id", "linked_entity_type"],
  });
};

export const useLinkPatientProvider = (
  patientId: string,
  linkedEntityType: EPatientLinkedEntity
) => {
  return useGenericMutation<TPatient, string | null>({
    mutationFn: (targetId) => LinkService.linkProvider(patientId, linkedEntityType, targetId),
    feature: "patient.linkProvider",
  });
};

export const usePatientForSelect = (
  extraFilters: Partial<TPatientSearchFilters> = {},
  enabled: MaybeRefOrGetter<boolean> = true
): TSelectFieldDynamicProps<TPatient, TPatientSearchFilters> => {
  const query = usePatientsPaginatedFiltered({
    initial: {
      search: null,
      page_size: 10,
      type: EPatientType.PATIENT,
      ...extraFilters,
    },
    // Getter, not `toValue` — resolving here would freeze a reactive `enabled` at its
    // setup value and the select would never follow it.
    enabled: () => toValue(enabled),
  });
  return {
    paginatedQuery: query,
    queryFilters: query.filters,
    queryEnabled: query.queryEnabled,
  };
};
